import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { IDataPoint } from './datapoints-data.service';
import { LevelAsset } from './assets-data.service';
import { OfflineEvent } from './events-data.service';

/**
 * WebhookSensor
 */
export class WebhookSensor {
	public publicSensorID: string;
	public sensorName: string;
	public sensorDescription: string;
	public type: string;
	public neuronSensorType: number = 0;
	public dataPoints: SensorDatapoint[];

	constructor(init?: Partial<WebhookSensor>) {
		Object.assign(this, init);
	}
}

/**
 * SensorDatapoint
 */
export class SensorDatapoint {
	/**
	 * publicSenorID
	 * 
	 * Yes::: 
	 * It does have a typo, but it is on the Azure Functions and SQL, so cant fix yet
	 */
	public publicSenorID: string = '';

	/**
	 * sensorName
	 */
	public name: string = '';

	/**
	 * pvName
	 */
	public pvName: string = '';

	/**
	 * description
	 */
	public description: string = '';

	/**
	 * pv
	 */
	public pv: number = 0;

	/**
	 * si
	 */
	public si: string = '';

	/**
	 * timestamp
	 */
	public timestamp: number = 0;

	/**
	 * value
	 */
	public value: number = 0;

	/**
	 * Neuron Sensor Type
	 */
	public neuronSensorType: number = 0;

	constructor(init?: Partial<SensorDatapoint>) {
		Object.assign(this, init);
	}
}

/**
 * Interface for Database Account Object
 * @help
 * let account = this.accounts.find(({ id }) => id === "<ID String>")
 */
export interface IIntegratonSensor {
	/** Sensor ID */
	SensorID: number
	/** Sensor ID */
	WebhookSensorID: number
	/** Sensor Public ID */
	PublicSensorID: string
	/** Sensor Name */
	Name: string
	/** Sensor Description */
	Description: string
	/** Sensor Image URL */
	ImageURL: string
	/** Sensor Webhook ID */
	WebhookID: number
	/** Sensor Enabled */
	Enabled: boolean
	/** Sensor Offline */
	Offline: boolean
	/** Sensor Modified Epoch */
	Modified: number
	/** Sensor Datapoints */
	Datapoints: IDataPoint[]
	/* Last Value Received Epoch */
	LastValueReceived: number
	/** Enable Offline Notifications*/
	EnableOffline: boolean;
	/** Linked Assets */
	LinkedAssets: LevelAsset[];
	/** Offline History */
	OfflineHistory: OfflineEvent[];
	/** Is Cumulative */
	isCumulative: boolean;
	/** Is Neuron Sensor */
	NeuronSensors: boolean;


	/** Optional Selected Property used purly for GUI */
	_Selected?: boolean;
}

/**
 * IntegrationSensorsService
 */
@Injectable({
	providedIn: 'root'
})
export class IntegrationSensorsService {

	/** Enable Console Messages */
	private Debug: boolean = false

	/** Default Account */
	private defaultAccountSubject: BehaviorSubject<any>;

	/** Default Account for User (JSON FORMAT) */
	public defaultAccount$: Observable<string>;

	/**
	 * Log message to the Console
	 * @param str
	 */
	consoleLog(str: any) { if (this.Debug == true) console.log(str) }

	/** API Route */
	apiRoute: string = "api/{0}/organisation/{1}/Sensors"

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private http: HttpClient,
		private globalFunctions: GlobalFunctions
	) { }

	/**
	 * Service to return a list of Webhook Integration for this Organistion
	 * @param accountGUID Account GUID
	 * @param organisationID Organisadion UID
	   * @param integrationID Integration ID
	 * @param queryParams Optional Query Parameters
	 * @returns IntegrationSensor[] Array of Sensors
	 */
	getIntegrationSensors$(accountGUID: string, organisationID: string, integrationID: string, queryParams: {} = {}): Observable<any> {

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/integration/${integrationID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams)
	}

	/**
	 * Service to return a list of Webhook Integration for this Organistion
	 * @param accountGUID Account GUID
	 * @param organisationID Organisadion UID
	 * @param integrationID Integrtion ID
	 * @param sensor sensor to patch
	 * @param queryParams Optional Query Parameters
	 * @returns IntegrationSensor[] Array of Sensors
	 */
	patchIntegrationSensor$(accountGUID: string, organisationID: string, integrationID: string, sensor: IIntegratonSensor): Observable<any> {

		let data = JSON.stringify(sensor);

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/integration/${integrationID}/sensor/${sensor.SensorID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

	/** Delete a sensor from a webhook integration. */
	deleteIntegrationSensor$(accountGUID: string, organisationID: string, integrationID: string, sensorID: number): Observable<any> {

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/integration/${integrationID}/sensor/${sensorID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", {});
	}

	/**
	 * getIntegrationSensorDetails
	 * @param accountGUID accountGUID 
	 * @param organisationID organisationID 
	 * @param sensorID sensorID 
	 * @returns 
	 */
	getIntegrationSensorDetails$(accountGUID: string, organisationID: string, sensorID: number): Observable<any> {

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/sensorDetails/${sensorID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", {})
	}


	/**
	 * PutIntegrationSensor
	 * @param accountGUID 
	 * @param organisationID 
	 * @param integrationID 
	 * @param sensorID 
	 * @param sensorJSON 
	 * @returns 
	 */
	PutIntegrationSensor$(accountGUID: string, organisationID: string, integrationID: string, sensorID: string, sensorJSON: string): Observable<any> {

		let data = sensorJSON;

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/integration/${integrationID}/sensor?publicID=${sensorID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PUT", data);

	}

}
